<template>
    <section aria-labelledby="profile-overview">
        <div class="rounded-lg dark:bg-true-gray-800 bg-white overflow-hidden shadow">
            <h2 class="sr-only" id="profile-overview-title">Profile Overview</h2>
            <div class="dark:bg-true-gray-800 bg-white p-6">
                <div class="">
                    <div class="flex flex-col">
                        <div class="flex-shrink-0">
                            <img v-if="user.photo" class="object-cover mx-auto h-20 w-20 rounded-full" :src="user.photo"
                                alt="" />
                            <DefaultUser class="block mx-auto h-20 w-20" v-else />
                        </div>
                        <div class="mt-4 text-center">
                            <p class="text-sm font-medium text-warm-gray-500">Welcome back,</p>
                            <div class="flex justify-center items-center">
                                <p
                                    class="text-xl font-bold sm:text-2xl dark:text-warm-gray-300 text-warm-gray-800 flex flex-inline items-center space-x-1">
                                    <span>{{ user.firstname }} {{ user.lastname }}</span>
                                    <BadgeCheckIcon v-if="user.license && user.license.isVerified"
                                        class="w-5 h-5 text-blue-500" />
                                </p>
                                <router-link to="/account" @click="mobileMenuOpen = false">
                                    <PencilAltIcon
                                        class="ml-2 mb-1 inline-block w-4 h-4 dark:text-warm-gray-400 text-warm-gray-600" />
                                </router-link>
                            </div>
                            <p class="text-sm font-medium dark:text-warm-gray-400 text-warm-gray-600">{{
                                getSchoolById(schools, user.school).name }} - {{ (user.course.includes("(AE)")) ?
                                    user.course.substr(-3, 2) : user.course.substr(-4, 3) }}</p>
                        </div>
                    </div>

                    <!-- Badges -->
                    <div class="my-4 flex flex-row -space-x-2 justify-center">

                        <!-- AMT -->
                        <div v-if="(user.achievements && user.achievements.includes('full-master-amt'))" :class="[
                            (user.achievements && user.achievements.includes('full-master-amt'))
                                ? 'bg-yellow-200 text-yellow-500 dark:bg-yellow-300 dark:text-yellow-500 '
                                : 'bg-true-gray-200 text-true-gray-300 dark:bg-true-gray-700 dark:text-true-gray-600 '
                            , 'flex-grow-0 rounded-full w-16 h-16 p-3 border-4 border-solid dark:border-true-gray-800 border-white'
                        ]">
                            <AllMasterLogo />
                        </div>
                        <div v-else-if="(user.achievements && user.achievements.includes('full-speedrunner-amt'))"
                            :class="[
                                (user.achievements && user.achievements.includes('full-speedrunner-amt'))
                                    ? 'bg-yellow-200 text-yellow-500 dark:bg-yellow-300 dark:text-yellow-500 '
                                    : 'bg-true-gray-200 text-true-gray-300 dark:bg-true-gray-700 dark:text-true-gray-600 '
                                , 'flex-grow-0 rounded-full w-16 h-16 p-3 border-4 border-solid dark:border-true-gray-800 border-white'
                            ]">
                            <AllSpeedrunnerLogo />
                        </div>
                        <div v-else :class="[
                            (user.achievements && user.achievements.includes('full-passer-amt'))
                                ? 'bg-yellow-200 text-yellow-500 dark:bg-yellow-300 dark:text-yellow-500 '
                                : 'bg-true-gray-200 text-true-gray-300 dark:bg-true-gray-700 dark:text-true-gray-600 '
                            , 'flex-grow-0 rounded-full w-16 h-16 p-3 border-4 border-solid dark:border-true-gray-800 border-white'
                        ]">
                            <AllPasserLogo />
                        </div>

                        <!-- AVT -->
                        <div v-if="(user.achievements && user.achievements.includes('full-master-avt'))" :class="[
                            (user.achievements && user.achievements.includes('full-master-avt'))
                                ? 'bg-yellow-200 text-yellow-500 dark:bg-yellow-300 dark:text-yellow-500 '
                                : 'bg-true-gray-200 text-true-gray-300 dark:bg-true-gray-700 dark:text-true-gray-600 '
                            , 'flex-grow-0 rounded-full w-16 h-16 p-3 border-4 border-solid dark:border-true-gray-800 border-white'
                        ]">
                            <AllMasterLogoHeli />
                        </div>
                        <div v-else-if="(user.achievements && user.achievements.includes('full-speedrunner-avt'))"
                            :class="[
                                (user.achievements && user.achievements.includes('full-speedrunner-avt'))
                                    ? 'bg-yellow-200 text-yellow-500 dark:bg-yellow-300 dark:text-yellow-500 '
                                    : 'bg-true-gray-200 text-true-gray-300 dark:bg-true-gray-700 dark:text-true-gray-600 '
                                , 'flex-grow-0 rounded-full w-16 h-16 p-3 border-4 border-solid dark:border-true-gray-800 border-white'
                            ]">
                            <AllSpeedrunnerLogoHeli />
                        </div>
                        <div v-else :class="[
                            (user.achievements && user.achievements.includes('full-passer-avt'))
                                ? 'bg-yellow-200 text-yellow-500 dark:bg-yellow-300 dark:text-yellow-500 '
                                : 'bg-true-gray-200 text-true-gray-300 dark:bg-true-gray-700 dark:text-true-gray-600 '
                            , 'flex-grow-0 rounded-full w-16 h-16 p-3 border-4 border-solid dark:border-true-gray-800 border-white'
                        ]">
                            <AllPasserLogoHeli />
                        </div>

                        <div
                            class="flex-grow-0 rounded-md w-16 h-16 border-4 border-solid dark:border-true-gray-800 border-white">
                            <router-link :to="`/school/${getSchoolById(schools, user.school).shorthand.toLowerCase()}`"
                                class="hover:text-md">
                                <img class="object-cover mx-auto h-14 w-14 rounded-md"
                                    :src="getSchoolById(schools, user.school).logoUrl" alt="" />
                            </router-link>

                        </div>
                    </div>
                </div>
            </div>
            <!-- User Stats -->
            <div
                class="border-t dark:border-true-gray-700 border-true-gray-100 bg-black-300 grid grid-cols-1 divide-y dark:divide-true-gray-700 divide-true-gray-100 sm:grid-cols-3 sm:divide-y-0 sm:divide-x">
                <div
                    class="px-6 py-5 text-warm-gray-800 dark:text-warm-gray-200 text-md font-medium text-center flex flex-inline items-center justify-center space-x-1">
                    <PresentationChartLineIcon class="w-5 h-5" />
                    <span class="text-warm-gray-800 dark:text-warm-gray-200">{{ (user.rank) ? this.getOrdinal(user.rank)
                        :
                        '' }}</span>
                    <span class="sm:hidden dark:text-warm-gray-400 text-warm-gray-700">Overall Rank</span>
                </div>
                <div
                    class="px-6 py-5 text-warm-gray-800 dark:text-warm-gray-200 text-md font-medium text-center flex flex-inline items-center justify-center space-x-1">
                    <Logo class="w-5 h-5" />
                    <span class="text-warm-gray-800 dark:text-warm-gray-200">{{ user.skillpoints.toLocaleString() || 0
                    }}</span>
                    <span class="sm:hidden dark:text-warm-gray-400 text-warm-gray-700">Skillpoints</span>
                </div>
                <div
                    class="px-6 py-5 text-warm-gray-800 dark:text-warm-gray-200 text-md font-medium text-center flex flex-inline items-center justify-center space-x-1">
                    <PaperAirplaneIcon class="w-5 h-5" />
                    <span class="text-warm-gray-800 dark:text-warm-gray-200">{{ (user.achievements) ?
                        user.achievements.length : '' }}</span>
                    <span class="sm:hidden dark:text-warm-gray-400 text-warm-gray-700">Achievements</span>
                </div>
            </div>
        </div>
    </section>
</template>
<script>
import Logo from "@/components/Logo"
import DefaultUser from "@/components/DefaultUser"
import { PresentationChartLineIcon, PencilAltIcon, PaperAirplaneIcon } from '@heroicons/vue/outline'
import { BadgeCheckIcon } from '@heroicons/vue/solid'
import { schoolService } from "@/utils/school"

import AllPasserLogo from "@/components/AllPasserLogo"
import AllSpeedrunnerLogo from "@/components/AllSpeedrunnerLogo"
import AllMasterLogo from "@/components/AllMasterLogo"
import AllPasserLogoHeli from "@/components/AllPasserLogoHeli"
import AllSpeedrunnerLogoHeli from "@/components/AllSpeedrunnerLogoHeli"
import AllMasterLogoHeli from "@/components/AllMasterLogoHeli"

export default {
    components: {
        Logo,
        DefaultUser,
        PresentationChartLineIcon,
        PencilAltIcon,
        PaperAirplaneIcon,
        BadgeCheckIcon,
        AllPasserLogo,
        AllSpeedrunnerLogo,
        AllMasterLogo,
        AllPasserLogoHeli,
        AllSpeedrunnerLogoHeli,
        AllMasterLogoHeli
    },
    props: {
        user: {
            type: Object,
            required: true
        },
        schools: {
            type: Array,
            required: true
        }
    },
    methods: {
        getSchoolById: schoolService.getSchoolById,
        getOrdinal: function(i) {
            var j = i % 10, k = i % 100;
            if (j == 1 && k != 11) { return i + "st"; }
            if (j == 2 && k != 12) { return i + "nd"; }
            if (j == 3 && k != 13) { return i + "rd"; }
            return i + "th";
        }
    }
}
</script>
    